import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from '@mui/material';
import color from 'utils/colors';
import { Delete, Edit, FileUpload } from '@mui/icons-material';
import { styled } from '@mui/styles';
import TableCell from '@mui/material/TableCell';
import CustomTextField from 'commons/components/CustomTextField/CustomTextField';
import { useDispatch, useSelector } from 'react-redux';
import { appSetError } from 'commons/commons.actions';
import { isZippedFile } from 'utils/functions';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function DocumentStepThree(props) {
  const { attachmentsArray, setAttachmentsArray } = props;

  const dispatch = useDispatch();
  const { user: currentUser } = useSelector((state) => state.app);

  const removeFileRow = (idx) => {
    const attachmentsRows = [...attachmentsArray];
    attachmentsRows.splice(idx, 1);
    setAttachmentsArray(attachmentsRows);
  };

  const setFileRow = (idx) => {
    // Funcionalidad para editar archivo individual
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.ppt,.pptx,.xlsx,.doc,.docx,.xls,.png,.jpg';
    fileInput.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const isFileAllowed = !isZippedFile(file.name);
        const existingFileNames = attachmentsArray.map(att => att.publicAtt?.name).filter(Boolean);
        const isDuplicate = existingFileNames.includes(file.name) && attachmentsArray[idx].publicAtt?.name !== file.name;
        
        if (!isFileAllowed) {
          dispatch(appSetError({ 
            msg: 'El archivo no puede ser comprimido' 
          }));
        } else if (isDuplicate) {
          dispatch(appSetError({ 
            msg: 'Ya existe un archivo con ese nombre' 
          }));
        } else {
          const updatedArray = [...attachmentsArray];
          updatedArray[idx] = { publicAtt: file, privateAtt: null };
          setAttachmentsArray(updatedArray);
        }
      }
    };
    fileInput.click();
  };

  const handleFileChange = ({ target: { files } }) => {
    const selectedFiles = Array.from(files);
    const validFiles = [];
    const invalidFiles = [];
    const duplicateFiles = [];
    
    // Obtener nombres de archivos existentes
    const existingFileNames = attachmentsArray.map(att => att.publicAtt?.name).filter(Boolean);
    
    selectedFiles.forEach((file) => {
      const isFileAllowed = !isZippedFile(file.name);
      const isDuplicate = existingFileNames.includes(file.name);
      
      if (!isFileAllowed) {
        invalidFiles.push(file.name);
      } else if (isDuplicate) {
        duplicateFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });
    
    if (invalidFiles.length > 0) {
      dispatch(appSetError({ 
        msg: `Los siguientes archivos no pueden ser comprimidos: ${invalidFiles.join(', ')}` 
      }));
    }
    
    if (duplicateFiles.length > 0) {
      dispatch(appSetError({ 
        msg: `Los siguientes archivos ya existen: ${duplicateFiles.join(', ')}` 
      }));
    }
    
    if (validFiles.length > 0) {
      const newAttachments = validFiles.map((file) => ({
        publicAtt: file,
        privateAtt: null
      }));
      setAttachmentsArray(prevArray => [...prevArray, ...newAttachments]);
    }
    
    // Limpiar el input
    document.getElementById('attachmentFiles').value = null;
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          mb: 2,
        }}>
        <CustomTextField
          name="attachmentFiles"
          label={(currentUser?.type === 'admin' || currentUser?.type === 'internal') ? 
            'Seleccionar archivos adjuntos (uno o varios)' : 
            'Seleccionar archivo adjunto (uno)'}
          value=''
          placeholder="Ningún archivo seleccionado"
          inputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <label htmlFor="attachmentFiles">
                  <input
                    accept=".pdf,.ppt,.pptx,.xlsx,.doc,.docx,.xls,.png,.jpg"
                    id="attachmentFiles"
                    name="attachmentFiles"
                    type="file"
                    multiple={currentUser?.type === 'admin' || currentUser?.type === 'internal'}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <IconButton
                    sx={{ color: color.blue }}
                    aria-label="attachmentFiles"
                    component="span">
                    <FileUpload />
                  </IconButton>
                </label>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ height: 300, overflow: 'auto', border: `1px solid ${color.blue}`, borderRadius: 1 }}>
        <TableContainer component={Paper} sx={{ height: '100%' }}>
          <Table sx={{ maxWidth: 700 }} size="small" aria-label="customized table" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell />
                <StyledTableCell>Adjunto (versión pública)</StyledTableCell>
                {/* INFO: Cliente requiere retirar la opción de agregar archivos confidenciales
                <StyledTableCell align="right">Versión confidencial</StyledTableCell> */}
                <StyledTableCell align="center">Acciones</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attachmentsArray.map((a, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <StyledTableRow key={`${attachmentsArray[idx].publicAtt?.name} ${idx}`}>
                  <StyledTableCell component="th" scope="row">
                    {`${idx + 1}.`}
                  </StyledTableCell>
                  <StyledTableCell>{attachmentsArray[idx].publicAtt?.name}</StyledTableCell>
                  {/* INFO: Cliente requiere retirar la opción de agregar archivos confidenciales
                  <StyledTableCell align="center">
                    {attachmentsArray[idx].privateAtt?.name || '-'}
                  </StyledTableCell> */}
                  <StyledTableCell align="right">
                    <IconButton
                      sx={{ color: color.blue }}
                      aria-label="."
                      component="span"
                      onClick={() => setFileRow(idx)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      sx={{ color: color.blue }}
                      aria-label="."
                      component="span"
                      onClick={() => removeFileRow(idx)}>
                      <Delete />
                    </IconButton>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
}

DocumentStepThree.propTypes = {
  attachmentsArray: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setAttachmentsArray: PropTypes.func.isRequired,
};

export default DocumentStepThree;
